import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatDate, formatDateTime, parseDateOnly } from '@/lib/date';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useDocuments } from '@/hooks/use-documents';
import {
  useCanModifyEmployee,
  useCanViewCompensations,
  useIsSuperAdmin,
  usePermission,
} from '@/lib/permissions';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/utils';
import type { DocumentFolder } from '@/types/document';
import type { EmployeeDetail } from '@/types/employee';
import {
  deriveDisplayedEmployeeStatus,
  type DisplayedEmployeeStatus,
} from '@/features/people/lib/employee-status';
import {
  buildProfileTabs,
  type ProfileTabAccess,
  type ProfileTabId,
} from '@/features/people/lib/profile-tabs';
import { IconActionButton } from './icon-action-button';
import {
  Briefcase,
  Calendar,
  CalendarDays,
  Check,
  Clock,
  Copy,
  DollarSign,
  FileText,
  Heart,
  Eye,
  KeyRound,
  Mail,
  MapPin,
  Monitor,
  Pencil,
  Phone,
  Trash2,
  UserCheck,
  UserX,
  Camera,
  History,
} from 'lucide-react';
import { useState } from 'react';

interface ProfileCardProps {
  employee: EmployeeDetail;
  activeTab: ProfileTabId;
  onTabChange: (tab: ProfileTabId) => void;
  onEdit?: () => void;
  onEditAvatar?: () => void;
  onImpersonate?: () => void;
  onDeactivate?: () => void;
  onDelete?: () => void;
  /**
   * Which of Documents, Assets and Access this reader may open; the page's
   * `?tab=` fallback reads the same value.
   */
  tabAccess: ProfileTabAccess;
  assetCount?: number;
  accessCount?: number;
}

const TAB_ICONS: Record<ProfileTabId, typeof Briefcase> = {
  job: Briefcase,
  emergency: Heart,
  documents: FileText,
  assets: Monitor,
  access: KeyRound,
  notes: Pencil,
  absences: CalendarDays,
  compensations: DollarSign,
  history: History,
};

function getInitials(
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string {
  const f = firstName?.charAt(0) || '';
  const l = lastName?.charAt(0) || '';
  return `${f}${l}`.toUpperCase() || '?';
}

function getStatusConfig(displayed: DisplayedEmployeeStatus) {
  switch (displayed) {
    case 'probation':
      return { label: 'Probation', dotColor: 'es-dot-probation', className: 'es-probation' };
    case 'offboarding':
      return {
        label: 'Offboarding',
        dotColor: 'bg-orange-500',
        className: 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300',
      };
    case 'deactivated':
      return {
        label: 'Deactivated',
        dotColor: 'es-dot-terminated',
        className: 'es-terminated',
      };
    case 'onboarding':
      return { label: 'Onboarding', dotColor: 'es-dot-onboarding', className: 'es-onboarding' };
    case 'active':
    default:
      return { label: 'Active', dotColor: 'es-dot-active', className: 'es-active' };
  }
}

function calculateTenure(
  startDate: string | Date | null | undefined,
  terminationDate?: string | Date | null
): string {
  if (!startDate) return '-';

  // Date-only fields (@db.Date, UTC midnight) → local calendar day via parseDateOnly,
  // so getMonth/getDate below don't shift back a day — and thus a whole month — west of UTC.
  const toDay = (value: string | Date) =>
    typeof value === 'string' ? parseDateOnly(value) : value;
  const start = toDay(startDate);
  const end = terminationDate ? toDay(terminationDate) : new Date();

  if (isNaN(start.getTime())) return '-';

  const years = end.getFullYear() - start.getFullYear();
  const months = end.getMonth() - start.getMonth();

  let totalMonths = years * 12 + months;

  // Adjust if the day hasn't passed yet this month
  if (end.getDate() < start.getDate()) {
    totalMonths--;
  }

  const finalYears = Math.floor(totalMonths / 12);
  const finalMonths = totalMonths % 12;

  if (finalYears === 0 && finalMonths === 0) {
    return 'Less than 1 month';
  }

  const parts: string[] = [];
  if (finalYears > 0) {
    parts.push(`${finalYears} ${finalYears === 1 ? 'year' : 'years'}`);
  }
  if (finalMonths > 0) {
    parts.push(`${finalMonths} ${finalMonths === 1 ? 'month' : 'months'}`);
  }

  return parts.join(' ');
}

function formatLastSeen(date: string | Date | null | undefined): string | null {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;

  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDateTime(date, { day: 'numeric', month: 'short', year: 'numeric' });
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Failed to copy - silently fail
    }
  };

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleCopy}
            className="ml-1 p-1 rounded hover:bg-muted transition-colors opacity-0 group-hover/detail:opacity-100 cursor-pointer"
          >
            {copied ? (
              <Check className="h-3 w-3 s-sage-text" />
            ) : (
              <Copy className="h-3 w-3 text-muted-foreground" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {copied ? 'Copied!' : 'Copy'}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface DetailItemProps {
  icon: typeof Mail;
  label: string;
  value: string | null | undefined;
  href?: string;
  copyable?: boolean;
  external?: boolean;
}

function DetailItem({ icon: Icon, label, value, href, copyable, external }: DetailItemProps) {
  const displayValue = value || '-';
  // Check if value is an email (contains @)
  const isEmail = typeof value === 'string' && value.includes('@');

  return (
    <div className="group/detail flex items-start gap-2">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-hygge/10 text-hygge">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="flex items-center gap-1 min-w-0">
          {href ? (
            <a
              href={href}
              {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className={cn(
                'text-xs font-medium text-foreground hover:text-primary min-w-0 cursor-pointer',
                isEmail ? 'break-all' : 'truncate'
              )}
            >
              {displayValue}
            </a>
          ) : (
            <span
              className={cn(
                'text-xs font-medium text-foreground min-w-0',
                isEmail ? 'break-all' : 'truncate'
              )}
            >
              {displayValue}
            </span>
          )}
          {copyable && value && <CopyButton value={value} />}
        </div>
      </div>
    </div>
  );
}

export function ProfileCard({
  employee,
  activeTab,
  onTabChange,
  onEdit,
  onEditAvatar,
  onImpersonate,
  onDeactivate,
  onDelete,
  tabAccess,
  assetCount,
  accessCount,
}: ProfileCardProps) {
  const formatName = useEmployeeNameFormatter();
  const fullName = formatName({ firstName: employee.firstName, lastName: employee.lastName });
  const statusConfig = getStatusConfig(deriveDisplayedEmployeeStatus(employee));
  const canModify = useCanModifyEmployee();
  const isSuperAdmin = useIsSuperAdmin();
  const showCompensations = useCanViewCompensations();
  // Only ask for the document tree when the section is actually offered —
  // the count feeds its badge, and the request 403s for everyone else. [HW-785]
  const { data: documents } = useDocuments(employee.id, { enabled: tabAccess.canViewDocuments });

  const documentCount =
    documents?.reduce(
      (total: number, folder: DocumentFolder) => total + folder.documents.length,
      0
    ) || 0;
  const currentUser = useAuthStore((s) => s.user);
  const isSelf = currentUser?.employee?.id === employee.id;

  // Global permissions (for viewing any employee)
  const canViewAbsences = usePermission('absences.requests.view');
  const canViewEmergency = usePermission('employees.emergency.view');

  // Self permissions (for viewing own profile)
  const canViewAbsencesSelf = usePermission('employees.absences.view.self');

  const tabs = buildProfileTabs({
    access: tabAccess,
    documentCount,
    showCompensations,
    assetCount,
    accessCount,
    showAbsences: canViewAbsences || (isSelf && canViewAbsencesSelf),
    // Emergency contains private contact info — always visible on own profile,
    // otherwise gated on employees.emergency.view.
    showEmergency: canViewEmergency || isSelf,
  }).map((tab) => ({ ...tab, icon: TAB_ICONS[tab.id] }));

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
      {/* Responsive: column on mobile, row on desktop */}
      <div className="flex flex-col lg:flex-row">
        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Header section with gradient */}
          <div className="relative px-7 pt-6 pb-5 bg-gradient-to-br from-primary/5 to-transparent">
            {/* Google account status badge — intentionally not rendered here, shown inline with name instead */}
            {/* Actions */}
            <div className="absolute right-5 top-5 flex items-center gap-1.5">
              {onEdit && (
                <IconActionButton icon={Pencil} tooltip="Edit" onClick={() => onEdit?.()} />
              )}
              {onImpersonate && (
                <IconActionButton
                  icon={Eye}
                  tooltip="Login as Employee"
                  onClick={() => onImpersonate?.()}
                />
              )}
              {onDeactivate && (
                <IconActionButton
                  icon={employee.status === 'terminated' ? UserCheck : UserX}
                  tooltip={employee.status === 'terminated' ? 'Reactivate' : 'Deactivate'}
                  onClick={() => {
                    if (canModify) {
                      onDeactivate?.();
                    }
                  }}
                  disabled={!canModify}
                  variant={employee.status === 'terminated' ? 'success' : 'destructive'}
                />
              )}
              {onDelete && isSuperAdmin && (
                <IconActionButton
                  icon={Trash2}
                  tooltip="Delete"
                  onClick={() => onDelete?.()}
                  variant="destructive"
                />
              )}
            </div>

            {/* Avatar and info */}
            <div className="flex items-start gap-5">
              <div className="relative group">
                <Avatar
                  className="h-20 w-20 border-4 border-background shadow-lg"
                  key={employee.avatarUrl || 'no-avatar'}
                >
                  {employee.avatarUrl ? (
                    <AvatarImage src={employee.avatarUrl} alt={fullName} />
                  ) : null}
                  <AvatarFallback className="s-avatar-fallback text-2xl font-semibold">
                    {getInitials(employee.firstName, employee.lastName)}
                  </AvatarFallback>
                </Avatar>
                {/* Status indicator */}
                <div
                  className={cn(
                    'absolute bottom-0.5 right-0.5 h-3.5 w-3.5 rounded-full border-2 border-card',
                    statusConfig.dotColor
                  )}
                />
                {/* Edit avatar button */}
                {onEditAvatar && canModify && (
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => onEditAvatar?.()}
                          className="absolute bottom-0 left-0 h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary/90 cursor-pointer shadow-lg"
                        >
                          <Camera className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="text-xs">
                        Change Avatar
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>

              <div className="min-w-0 pt-1">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h1 className="text-xl font-bold tracking-tight text-foreground">{fullName}</h1>
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-medium',
                      statusConfig.className
                    )}
                  >
                    <span className={cn('h-1.5 w-1.5 rounded-full', statusConfig.dotColor)} />
                    {statusConfig.label}
                    {employee.status === 'probation' && employee.probationEndDate && (
                      <span className="opacity-80 font-normal">
                        (until {formatDate(employee.probationEndDate, 'dayMonth') || '-'})
                      </span>
                    )}
                  </span>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={cn(
                            'h-5 w-5 rounded-full flex items-center justify-center',
                            employee.user?.googleId
                              ? 's-sage-bg-soft s-sage-text'
                              : 'bg-muted text-muted-foreground'
                          )}
                        >
                          {employee.user?.googleId ? (
                            <Check className="h-3 w-3 stroke-[2.5]" />
                          ) : (
                            <UserX className="h-3 w-3" />
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="text-xs">
                        {employee.user?.googleId
                          ? 'Google Account connected'
                          : 'Google Account not connected'}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {employee.position?.title || 'No position'}
                </p>
                {employee.department?.name && (
                  <p className="text-xs font-medium text-muted-foreground">
                    {employee.department.name}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Details grid - 1 col on mobile, 2 cols on desktop */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-border/50 px-4 sm:px-7 py-5">
            {/* Personal info */}
            <DetailItem
              icon={Calendar}
              label="Date of Birth"
              value={formatDate(employee.personalInfo?.dateOfBirth, 'dayMonth') || '-'}
            />
            <DetailItem
              icon={MapPin}
              label="Location & Address"
              value={employee.personalInfo?.address}
            />
            <DetailItem
              icon={Briefcase}
              label="Tenure"
              value={calculateTenure(employee.startDate, employee.terminationDate)}
            />

            {/* Contact info */}
            <DetailItem
              icon={Mail}
              label="Work Email"
              value={employee.workEmail}
              href={`mailto:${employee.workEmail}`}
              copyable
            />
            <DetailItem
              icon={Mail}
              label="Personal Email"
              value={employee.personalInfo?.personalEmail}
              href={
                employee.personalInfo?.personalEmail
                  ? `mailto:${employee.personalInfo.personalEmail}`
                  : undefined
              }
              copyable
            />
            <DetailItem icon={Phone} label="Phone" value={employee.personalInfo?.phone} copyable />
            <DetailItem
              icon={FileText}
              label="Corporate CV"
              value={employee.personalInfo?.corporateCvUrl}
              href={employee.personalInfo?.corporateCvUrl ?? undefined}
              external
            />
            <DetailItem
              icon={Clock}
              label="Last Seen"
              value={formatLastSeen(employee.user?.lastLoginAt) ?? 'Never logged in'}
            />
          </div>
        </div>

        {/* Vertical tabs - horizontal on mobile, vertical on desktop */}
        <div className="w-full lg:w-40 shrink-0 border-t lg:border-t-0 lg:border-l border-border bg-muted/30 p-2 lg:p-3">
          <div className="hidden lg:block mb-2 px-2.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
            Sections
          </div>
          <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-x-visible">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={cn(
                  'flex items-center gap-2 rounded-lg px-2.5 lg:px-2.5 py-2 text-xs font-medium transition-colors whitespace-nowrap cursor-pointer',
                  activeTab === tab.id
                    ? 'bg-hygge/10 text-hygge'
                    : 'text-muted-foreground hover:bg-muted/40'
                )}
              >
                <tab.icon
                  className={cn(
                    'h-3.5 w-3.5',
                    activeTab === tab.id ? 'text-hygge' : 'text-muted-foreground'
                  )}
                />
                <span className="hidden sm:inline lg:inline">{tab.label}</span>
                {tab.badge && (
                  <span className="ml-auto rounded-full bg-cta px-1.5 py-0.5 text-[9px] font-semibold text-cta-foreground">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}
